import { GamIntegration } from '@pubstack/common/src/gam/adx'
import { MarketplaceBidder } from '@pubstack/common/src/marketplaceBidder'
import { ExclusionFileUploadAction, ExclusionType, GroupOverlap, RefreshException, RefreshGroup } from '@pubstack/common/src/refresh'
import { Scope } from '@pubstack/common/src/scope'
import { Site } from '@pubstack/common/src/site'
import { useFetch } from 'use-http'
import { useFetchWithException } from './api-access'

export const useScopes = (deps: unknown[] | null = []) => {
  const fetchAll = useFetch<Scope[]>('/scopes', { suspense: true }, deps || undefined)
  const fetch = useFetch<Scope>('/scopes/scope', { suspense: true }, deps || undefined)
  return { all: { ...fetchAll }, byId: { ...fetch } }
}

export const useSites = (deps: unknown[] | null = []) => {
  const fetchAll = useFetchWithException<Site[]>(`/sites`, deps || undefined)
  const getAll = (search: { getDisabledSites?: boolean; search?: string; getAdManagedSites?: boolean; getPbsStandaloneSites?: boolean }) => {
    const paramsObj = new URLSearchParams()
    if (search.search?.length) {
      paramsObj.append('search', search.search)
    }
    if (search.getDisabledSites) {
      paramsObj.append('getDisabledSites', String(search.getDisabledSites))
    }
    if (search.getAdManagedSites) {
      paramsObj.append('getAdManagedSites', String(search.getAdManagedSites))
    }
    if (search.getPbsStandaloneSites) {
      paramsObj.append('getPbsStandaloneSites', String(search.getPbsStandaloneSites))
    }
    return fetchAll.get(paramsObj.toString().length ? `?${paramsObj}` : '')
  }
  const fetch = useFetchWithException<Site>(`/sites`)
  const del = (site: Site) => fetch.delete(site.id)
  const put = (site: Site) => fetch.put(site.id, site)
  const getById = (id: Site['id']) => fetch.get(id)
  return { all: { ...fetchAll, get: getAll }, byId: { ...fetch, get: getById, put, del } }
}

export const useUserMetadata = (deps: unknown[] | null = []) => {
  return { byId: { ...useFetch('/users/me', {}, deps || undefined) } }
}

export const useMarketplaceRequestEmailQuery = (deps: unknown[] | null = []) => {
  return { byId: { ...useFetch<Scope>('/scopes/scope/request-marketplace-sent', {}, deps || undefined) } }
}

export const useRefreshGroups = (deps: unknown[] | null = []) => {
  const fetchAll = useFetchWithException<RefreshGroup[]>('/refresh/group', deps || undefined)
  const fetch = useFetchWithException<RefreshGroup>('/refresh/group', deps || undefined)
  const checkFetch = useFetch<void | { conflicts: GroupOverlap }>('/refresh/group', deps || undefined)
  const getById = (groupId: string) => fetch.get(groupId)
  const check = (refreshGroup: RefreshGroup) => checkFetch.post('/check', refreshGroup)
  return { all: { ...fetchAll }, byId: { ...fetch, get: getById }, check }
}

export const useRefreshException = (deps: unknown[] | null = []) => {
  const fetch = useFetchWithException<RefreshException>('/refresh/exceptions', deps || undefined)
  const fetchFiles = useFetchWithException<RefreshException>('/refresh/exceptions/files', deps || undefined)
  const downloadFiles = useFetchWithException<Buffer>('/refresh/exceptions/files', deps || undefined)

  const downloadFile = (type: ExclusionType) => {
    const params = new URLSearchParams()
    params.append('type', type)
    return downloadFiles.get(`?${params.toString()}`)
  }

  type RefreshExceptionFiles = {
    lineItemIdFile: File | null
    orderIdFile: File | null
    orderIdAction: ExclusionFileUploadAction
    lineItemIdAction: ExclusionFileUploadAction
    mode: string
  }

  /**
   * Takes care of updating exclusion files through sending multipart formData
   */
  const updateFiles = ({ lineItemIdFile, orderIdFile, orderIdAction, lineItemIdAction, mode }: RefreshExceptionFiles) => {
    const data = new FormData()
    data.append('orderIdAction', orderIdAction)
    data.append('lineItemIdAction', lineItemIdAction)
    if (orderIdFile) {
      data.append('orderIdFile', orderIdFile)
    }
    if (lineItemIdFile) {
      data.append('lineItemIdFile', lineItemIdFile)
    }
    data.append('mode', mode)
    return fetchFiles.post(data)
  }

  /**
   * Chains the two promises updating `refreshException` and its files separately and returning the last version of the updated `refreshException`
   */
  const updateRefreshException = async ({ refreshException, exceptionFiles }: { refreshException?: RefreshException; exceptionFiles?: RefreshExceptionFiles }) => {
    if (refreshException) {
      refreshException = await fetch.post(refreshException)
    }
    if (exceptionFiles) {
      refreshException = await updateFiles(exceptionFiles)
    }
    return refreshException
  }

  return { byId: { ...fetch }, downloadFile, updateRefreshException, filesLoading: fetchFiles.loading, filesAbort: fetchFiles.abort }
}

export const useIntegration = (deps: unknown[] | null = []) => {
  return { byId: { ...useFetch<GamIntegration>(`/integrations/gam`, {}, deps || undefined) } }
}

export const useIntegrationMarketplace = (deps: unknown[] | null = []) => {
  const fetch = useFetchWithException<MarketplaceBidder[]>('/marketplace/integration', deps || undefined)
  const post = (bidder: MarketplaceBidder) => fetch.post(bidder)
  return { all: { ...fetch }, byId: { post } }
}
